import React, { useState, useEffect } from 'react';

interface PageInfo {
  route: string;
  component: string;
  description: string;
  features: string[];
  techStack: string[];
  apiEndpoints?: string[];
}

// Knowledge base of all our pages
const pageKnowledge: Record<string, PageInfo> = {
  '/bet-engineer': {
    route: '/bet-engineer',
    component: 'PlaceBetEngineer',
    description: 'Engineer mode for the betting flow. Shows exactly what happens when you place a bet, including all state changes, API calls, and system events.',
    features: [
      'Real-time state visualization',
      'API call monitoring',
      'Event tracking',
      'Mock betting simulation',
      'Balance updates',
      'Success/error handling'
    ],
    techStack: ['React', 'TypeScript', 'TailwindCSS', 'CloudFlare Workers'],
    apiEndpoints: ['/api/bets', '/api/users', '/api/markets']
  },
  '/training': {
    route: '/training',
    component: 'ChatTrainingDashboard',
    description: 'AI training platform showing how every chat conversation makes our bot smarter. View chat history, score quality, and manage training queue.',
    features: [
      'Chat conversation history',
      'Quality scoring (1-5 stars)',
      'Training queue management',
      'Real-time metrics',
      'Learning visualization'
    ],
    techStack: ['React', 'CloudFlare AI', 'D1 Database', 'Vectorize (planned)'],
    apiEndpoints: ['/api/training/chats', '/api/training/queue', '/api/training/score']
  },
  '/showcase': {
    route: '/showcase',
    component: 'Showcase',
    description: 'Gallery of all 25+ features built in the project. Filter by category, see development status, and explore the entire platform.',
    features: [
      'Feature gallery',
      'Category filtering',
      'Status indicators',
      'Feature statistics',
      'Quick navigation'
    ],
    techStack: ['React', 'TypeScript'],
    apiEndpoints: []
  },
  '/otp-login': {
    route: '/otp-login',
    component: 'OTPLoginFlow',
    description: 'Passwordless authentication using one-time passwords sent via email. Secure, simple, and user-friendly.',
    features: [
      'Email-based OTP',
      '6-digit verification codes',
      'Auto-resend capability',
      '10-minute code expiry',
      'Session persistence'
    ],
    techStack: ['React', 'CloudFlare Workers', 'Resend API', 'D1 Database'],
    apiEndpoints: ['/api/send-email', '/api/otp/verify', '/api/otp/store']
  },
  '/philosophy': {
    route: '/philosophy',
    component: 'Philosophy',
    description: 'Blog documenting our development philosophy: "The Philosophy of Tiny Steps" and "The Power of Unified Systems".',
    features: [
      'Development philosophy',
      'Case studies',
      'Engineering insights',
      'System thinking examples'
    ],
    techStack: ['React', 'Markdown'],
    apiEndpoints: []
  },
  '/': {
    route: '/',
    component: 'CreateMarket',
    description: 'Main prediction market platform. Create markets, place bets, track predictions, and compete with friends.',
    features: [
      'Market creation',
      'Bet placement',
      'Portfolio tracking',
      'Real-time updates',
      'Guest mode',
      'Engineer mode toggle'
    ],
    techStack: ['React', 'CloudFlare Workers', 'D1 Database'],
    apiEndpoints: ['/api/markets', '/api/bets', '/api/users']
  }
};

const EngineeringAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState<Array<{role: string, content: string}>>([]);
  const [currentUrl, setCurrentUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    // Track current page
    setCurrentUrl(window.location.pathname);

    // Welcome message
    setConversation([{
      role: 'assistant',
      content: `🔧 Engineering Assistant ready! I can explain any page, component, or feature. Try asking:
• "What does this page do?"
• "Show me the tech stack"
• "What API endpoints are used?"
• "How does the betting flow work?"

Currently viewing: ${window.location.pathname}`
    }]);
  }, []);

  const analyzePage = (url: string) => {
    // Extract path from full URL if needed
    const path = url.includes('futr-bet.pages.dev')
      ? new URL(url).pathname
      : url;

    const pageInfo = pageKnowledge[path];

    if (pageInfo) {
      return {
        found: true,
        ...pageInfo
      };
    }

    return {
      found: false,
      message: `I don't have detailed information about ${path} yet, but I can help you explore it!`
    };
  };

  const processMessage = async () => {
    if (!message.trim()) return;

    const userMessage = message;
    setMessage('');
    setConversation(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsAnalyzing(true);

    // Simple intent detection
    const lowerMessage = userMessage.toLowerCase();
    let response = '';

    if (lowerMessage.includes('what does') || lowerMessage.includes('what is') || lowerMessage.includes('explain')) {
      // Explain current page or a specific URL
      const urlMatch = userMessage.match(/https?:\/\/[^\s]+/);
      const targetUrl = urlMatch ? urlMatch[0] : currentUrl;

      const analysis = analyzePage(targetUrl);

      if (analysis.found) {
        response = `📄 **${analysis.component}** (${analysis.route})

**Purpose:** ${analysis.description}

**Key Features:**
${analysis.features?.map(f => `• ${f}`).join('\n')}

**Tech Stack:** ${analysis.techStack?.join(', ')}
${analysis.apiEndpoints?.length ? `\n**API Endpoints:**\n${analysis.apiEndpoints.map(e => `• ${e}`).join('\n')}` : ''}`;
      } else {
        response = analysis.message || 'Page not found in knowledge base.';
      }
    } else if (lowerMessage.includes('tech stack')) {
      const analysis = analyzePage(currentUrl);
      if (analysis.found) {
        response = `🛠️ **Tech Stack for ${analysis.component}:**
${analysis.techStack?.map(t => `• ${t}`).join('\n')}`;
      }
    } else if (lowerMessage.includes('api') || lowerMessage.includes('endpoint')) {
      const analysis = analyzePage(currentUrl);
      if (analysis.found && analysis.apiEndpoints?.length) {
        response = `🔌 **API Endpoints used:**
${analysis.apiEndpoints.map(e => `• ${e}`).join('\n')}`;
      } else {
        response = 'This page doesn\'t use API endpoints directly.';
      }
    } else if (lowerMessage.includes('betting flow') || lowerMessage.includes('bet flow')) {
      response = `🎰 **Betting Flow Architecture:**

1. **User Clicks "Place Bet"**
   - Validate bet amount
   - Check user authentication
   - Verify sufficient balance

2. **API Call to CloudFlare Worker**
   - POST /api/bets
   - Include: user_id, market_id, option_id, amount

3. **Worker Processing**
   - Validate all parameters
   - Begin D1 transaction
   - Update user balance
   - Create bet record
   - Update market statistics

4. **Response & UI Update**
   - Show success modal
   - Update local balance
   - Add to bet history
   - Trigger confetti animation

**Engineer Mode:** Visit /bet-engineer to see this in real-time!`;
    } else if (lowerMessage.includes('how') || lowerMessage.includes('work')) {
      response = `I can help you understand how things work! Try asking:
• "How does the OTP login work?"
• "How does the AI chat learn?"
• "How are markets created?"
• "How is data stored?"

Or visit /bet-engineer to see the betting flow in action!`;
    } else {
      // Call our AI endpoint for general questions
      try {
        const aiResponse = await fetch('https://futr-bet-api.b-9f2.workers.dev/api/ai/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: `As an engineering assistant for FutrBet, answer: ${userMessage}`,
            engineerMode: true
          })
        });
        const data = await aiResponse.json();
        response = data.response || 'Let me help you explore the codebase!';
      } catch (error) {
        response = 'I can help you understand any part of FutrBet! Try asking about specific pages or features.';
      }
    }

    setIsAnalyzing(false);
    setConversation(prev => [...prev, { role: 'assistant', content: response }]);
  };

  return (
    <>
      {/* Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-4 bg-yellow-500 text-black rounded-full p-4 shadow-lg hover:bg-yellow-400 transition-all hover:scale-110 z-50"
          title="Engineering Assistant"
        >
          <span className="text-2xl">🔧</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 w-96 h-[600px] bg-black border-2 border-yellow-500 rounded-lg shadow-2xl z-50 flex flex-col">
          {/* Header */}
          <div className="bg-yellow-500 text-black p-4 rounded-t-lg flex justify-between items-center">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🔧</span>
              <div>
                <div className="font-bold">Engineering Assistant</div>
                <div className="text-xs">Ask me about any page or component</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-yellow-600 rounded p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Current Page Indicator */}
          <div className="bg-gray-900 px-4 py-2 border-b border-gray-700">
            <div className="text-xs text-gray-400">Currently viewing:</div>
            <div className="text-sm text-yellow-400 font-mono">{currentUrl}</div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-900">
            {conversation.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-800 text-yellow-400 border border-yellow-500'
                  }`}
                >
                  <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                </div>
              </div>
            ))}
            {isAnalyzing && (
              <div className="flex justify-start">
                <div className="bg-gray-800 text-yellow-400 p-3 rounded-lg border border-yellow-500">
                  <div className="flex gap-1">
                    <span className="animate-bounce">.</span>
                    <span className="animate-bounce delay-100">.</span>
                    <span className="animate-bounce delay-200">.</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="px-4 py-2 bg-gray-800 border-t border-gray-700">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  setMessage('What does this page do?');
                  setTimeout(processMessage, 100);
                }}
                className="text-xs px-2 py-1 bg-gray-700 text-yellow-400 rounded hover:bg-gray-600"
              >
                Explain Page
              </button>
              <button
                onClick={() => {
                  setMessage('Show me the tech stack');
                  setTimeout(processMessage, 100);
                }}
                className="text-xs px-2 py-1 bg-gray-700 text-yellow-400 rounded hover:bg-gray-600"
              >
                Tech Stack
              </button>
              <button
                onClick={() => {
                  setMessage('What API endpoints are used?');
                  setTimeout(processMessage, 100);
                }}
                className="text-xs px-2 py-1 bg-gray-700 text-yellow-400 rounded hover:bg-gray-600"
              >
                API Endpoints
              </button>
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-700 bg-gray-900">
            <div className="flex gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && processMessage()}
                placeholder="Ask about code, pages, or features..."
                className="flex-1 bg-black border border-yellow-500 text-yellow-400 px-3 py-2 rounded focus:outline-none focus:border-yellow-300"
                disabled={isAnalyzing}
              />
              <button
                onClick={processMessage}
                disabled={isAnalyzing || !message.trim()}
                className="bg-yellow-500 text-black px-4 py-2 rounded hover:bg-yellow-400 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
              >
                Ask
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EngineeringAssistant;